import {
  ListAPInvoicesInputSchema,
  MAX_AP_INVOICE_SUPPLIER_FILTER_IDS,
  MAX_SHARED_AP_INVOICE_IDS,
} from '@erp/rpc';
import { z } from 'zod';

const invoiceIdSchema = z.uuid();
const supplierIdSchema = z.uuid();

export const apInvoicesSearchSchema = z.object({
  invoiceNumber: z.string().optional(),
  supplierIds: z
    .array(supplierIdSchema)
    .max(MAX_AP_INVOICE_SUPPLIER_FILTER_IDS)
    .optional(),
  status: z.array(z.string()).optional(),
  paymentStatus: z.array(z.string()).optional(),
  trouble: ListAPInvoicesInputSchema.shape.trouble,
  invoiceDateFrom: z.string().optional(),
  invoiceDateTo: z.string().optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  receivedDateFrom: z.string().optional(),
  receivedDateTo: z.string().optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  /** Invoices to show for a shared link. Absent means the normal list. */
  invoiceIds: z
    .array(invoiceIdSchema)
    .max(MAX_SHARED_AP_INVOICE_IDS)
    .optional(),
  /** When true, the invoices list includes invoices that are still snoozed. */
  showSnoozed: z.boolean().optional(),
});

export type InvoicesSearchParams = z.infer<typeof apInvoicesSearchSchema>;

export type SavedInvoiceFilters = Omit<
  InvoicesSearchParams,
  'page' | 'pageSize' | 'invoiceIds' | 'showSnoozed'
>;

const STRING_FILTER_KEYS = [
  'invoiceNumber',
  'trouble',
  'invoiceDateFrom',
  'invoiceDateTo',
  'dueDateFrom',
  'dueDateTo',
  'receivedDateFrom',
  'receivedDateTo',
  'sortBy',
  'sortDirection',
] as const satisfies readonly (keyof SavedInvoiceFilters)[];

const LIST_FILTER_KEYS = [
  'status',
  'paymentStatus',
  'supplierIds',
] as const satisfies readonly (keyof SavedInvoiceFilters)[];

export const SavedInvoiceFiltersSchema = apInvoicesSearchSchema.omit({
  page: true,
  pageSize: true,
  invoiceIds: true,
  showSnoozed: true,
});

export const SavedInvoiceFilterStateSchema = z.object({
  filters: SavedInvoiceFiltersSchema,
  autoApply: z.boolean(),
});

export function buildSavedFilters(
  params: InvoicesSearchParams,
): SavedInvoiceFilters {
  const {
    page: _page,
    pageSize: _pageSize,
    invoiceIds: _invoiceIds,
    showSnoozed: _showSnoozed,
    ...filters
  } = params;
  return filters;
}

function asList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function readUuid(
  item: unknown,
  schema: z.ZodType<string>,
): string | undefined {
  if (typeof item !== 'string') return undefined;
  const parsed = schema.safeParse(item);
  if (!parsed.success) return undefined;
  return parsed.data.toLowerCase();
}

/** Valid unique ids, in order, capped so a shared URL stays within the API limit. */
function takeUniqueIds(
  values: readonly unknown[],
  schema: z.ZodType<string>,
  max: number,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of values) {
    const id = readUuid(item, schema);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length === max) break;
  }
  return ids;
}

function nonEmptyIds(ids: string[]): string[] | undefined {
  return ids.length > 0 ? ids : undefined;
}

/**
 * Invoice ids from a shared link. A single id, a JSON array, and a repeated
 * query param all work. Invalid ids are dropped; the same id in either hex
 * case collapses to one entry; the list stops at {@link MAX_SHARED_AP_INVOICE_IDS}
 * so the list request stays valid.
 */
export function parseSharedInvoiceIds(value: unknown): string[] | undefined {
  return nonEmptyIds(
    takeUniqueIds(asList(value), invoiceIdSchema, MAX_SHARED_AP_INVOICE_IDS),
  );
}

/**
 * Vendor ids from the invoices filter. A single id, a JSON array, a repeated
 * query param, and the legacy `supplierId` string all work. Invalid ids are
 * dropped; the same id in either hex case collapses to one entry.
 */
export function parseSupplierFilterIds(
  value: unknown,
  legacy?: unknown,
): string[] | undefined {
  return nonEmptyIds(
    takeUniqueIds(
      [...asList(value), ...asList(legacy)],
      supplierIdSchema,
      MAX_AP_INVOICE_SUPPLIER_FILTER_IDS,
    ),
  );
}

/** Fold a legacy single `supplierId` into `supplierIds` before schema parse. */
export function migrateInvoiceSupplierSearch(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const record = { ...(value as Record<string, unknown>) };
  if (!('supplierId' in record) && !('supplierIds' in record)) {
    return value;
  }
  const supplierIds = parseSupplierFilterIds(
    record.supplierIds,
    record.supplierId,
  );
  delete record.supplierId;
  if (supplierIds) {
    record.supplierIds = supplierIds;
  } else {
    delete record.supplierIds;
  }
  return record;
}

/**
 * Saved-filter storage: a filter state, a bare filter object, or the legacy
 * preset array. Each shape may still carry a single `supplierId`.
 */
export function prepareStoredInvoiceFilters(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return item;
      }
      const record = item as Record<string, unknown>;
      return {
        ...record,
        filters: migrateInvoiceSupplierSearch(record.filters),
      };
    });
  }
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  if ('filters' in record) {
    return {
      ...record,
      filters: migrateInvoiceSupplierSearch(record.filters),
    };
  }
  return migrateInvoiceSupplierSearch(record);
}

/** Route search parser. One bad invoice id does not discard the rest of the URL. */
export function validateInvoicesSearch(
  search: Record<string, unknown>,
): InvoicesSearchParams {
  const migrated = migrateInvoiceSupplierSearch(search);
  const record =
    migrated && typeof migrated === 'object' && !Array.isArray(migrated)
      ? (migrated as Record<string, unknown>)
      : search;
  return apInvoicesSearchSchema.parse({
    ...record,
    invoiceIds: parseSharedInvoiceIds(record.invoiceIds),
  });
}

export function normalizeSavedInvoiceFilters(
  filters: SavedInvoiceFilters | null,
): SavedInvoiceFilters {
  const source = filters ?? {};
  const normalized: Record<string, unknown> = {};
  for (const key of STRING_FILTER_KEYS) {
    normalized[key] = source[key] || undefined;
  }
  for (const key of LIST_FILTER_KEYS) {
    const value = source[key];
    normalized[key] = value && value.length > 0 ? [...value].sort() : undefined;
  }
  return SavedInvoiceFiltersSchema.parse(normalized);
}

export function areSavedInvoiceFiltersEqual(
  left: SavedInvoiceFilters | null,
  right: SavedInvoiceFilters | null,
): boolean {
  return (
    JSON.stringify(normalizeSavedInvoiceFilters(left)) ===
    JSON.stringify(normalizeSavedInvoiceFilters(right))
  );
}

export function hasInvoiceListFilters(search: InvoicesSearchParams): boolean {
  return (
    Object.values(normalizeSavedInvoiceFilters(buildSavedFilters(search))).some(
      (value) => value !== undefined,
    ) || Boolean(search.invoiceIds?.length)
  );
}
